import {
    type AgentSavingsMode,
    type AgentSavingsMetricCard,
    AGENT_SAVINGS_METRIC_CARDS_CONFIG,
    DIY_COST,
    END_TO_END_COST,
} from "@/app/_libs/data/landing-page/agent-savings-section";

interface CalculateAgentSavingsParams {
    mode: AgentSavingsMode;
    estimatedSalePrice: number;
    agentCommission: number;
    agentMarketing: number;
}

interface AgentSavingsMetrics {
    agentCost: number;
    unreservedCost: number;
    yourSavings: number;
}

interface AgentSavingsResult {
    metrics: AgentSavingsMetrics;
    metricCards: AgentSavingsMetricCard[];
}

/**
 * Calculate agent vs Unreserved costs and derive display-ready metric cards.
 */
export function calculateAgentSavings(
    params: CalculateAgentSavingsParams,
): AgentSavingsResult {
    const {
        mode,
        estimatedSalePrice,
        agentCommission,
        agentMarketing,
    } = params;

    const isEndToEnd = mode === "endToEnd";

    // Agent cost = Sale Price × Agent commission + Agent marketing
    const agentCost =
        (estimatedSalePrice * agentCommission) / 100 + agentMarketing;

    // Unreserved cost based on mode
    const unreservedCost = isEndToEnd ? END_TO_END_COST : DIY_COST;

    // Your savings = Agent cost - Unreserved cost (minimum 0)
    const yourSavings = Math.max(0, agentCost - unreservedCost);

    const metrics: AgentSavingsMetrics = {
        agentCost: Math.round(agentCost),
        unreservedCost,
        yourSavings: Math.round(yourSavings),
    };

    const metricCards: AgentSavingsMetricCard[] =
        AGENT_SAVINGS_METRIC_CARDS_CONFIG.map((config) => ({
            id: config.id,
            label: config.label,
            amount:
                config.id === "agent-cost"
                    ? metrics.agentCost
                    : config.id === "unreserved"
                        ? metrics.unreservedCost
                        : metrics.yourSavings,
        }));

    return {
        metrics,
        metricCards,
    };
}


